import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import { ModalContext } from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { BulkCreateBrandProvidersParams } from '@hooks/useBrandProvider'
import type { ShowCreateBrandGroupProviderModalPayload } from '@hooks/useBrandGroupProvider'
import type { ShowCreateBrandGroupPublicationModalPayload } from '@hooks/useBrandGroupPublication'

import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateBrandGroupMembersModal from '@modals/CreateBrandGroupMembersModal'
import CreateBrandGroupProviderModal from '@modals/CreateBrandGroupProviderModal'
import CreateBrandGroupPublicationModal from '@modals/CreateBrandGroupPublicationModal'
import ManageActivityModal from '@modals/ManageActivityModal'

import { ActivityModel, BrandGroupMemberModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showBulkCreateBrandProviderModal: false,
  showCreateBrandGroupMembersModal: false,
  showCreateBrandGroupProviderModal: false,
  showCreateBrandGroupPublicationModal: false,
  showManageActivityModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = <ModalName extends keyof typeof componentCallbacks, >(component: ModalName, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateBrandProviderModal: {
      bulkCreateBrandProviders: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
    },
    CreateBrandGroupMembersModal: {
      bulkCreateBrandGroupMembers: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showCreateBrandGroupMembersModal: false }),
    },
    CreateBrandGroupProviderModal: {
      createBrandGroupProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showCreateBrandGroupProviderModal: false }),
    },
    CreateBrandGroupPublicationModal: {
      createBrandGroupPublication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showCreateBrandGroupPublicationModal: false }),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const { brandGroupId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateBrandProviderModal,
    showCreateBrandGroupMembersModal,
    showCreateBrandGroupProviderModal,
    showCreateBrandGroupPublicationModal,
    showManageActivityModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteBrandGroupMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteBrandGroupProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteBrandGroupPublication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setAdminBrandGroupMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateBrandProviderModal: (payload: BulkCreateBrandProvidersParams['bulkCreateParams']) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCreateBrandGroupMembersModal: (payload: { brandGroupMember: Partial<BrandGroupMemberModel> }) => {
        setModalData('CreateBrandGroupMembersModal', payload)
        setState({ showCreateBrandGroupMembersModal: true })
      },
      showCreateBrandGroupProviderModal: (payload: ShowCreateBrandGroupProviderModalPayload) => {
        setModalData('CreateBrandGroupProviderModal', payload)
        setState({ showCreateBrandGroupProviderModal: true })
      },
      showCreateBrandGroupPublicationModal: (payload: ShowCreateBrandGroupPublicationModalPayload) => {
        setModalData('CreateBrandGroupPublicationModal', payload)
        setState({ showCreateBrandGroupPublicationModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      updateBrandGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent brandGroupId={brandGroupId} />

        <BulkCreateBrandProviderModal
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateBrandGroupMembersModal
          callbacks={callbacks('CreateBrandGroupMembersModal', setState)}
          showModal={showCreateBrandGroupMembersModal}
        />

        <CreateBrandGroupProviderModal
          callbacks={callbacks('CreateBrandGroupProviderModal', setState)}
          showModal={showCreateBrandGroupProviderModal}
        />

        <CreateBrandGroupPublicationModal
          callbacks={callbacks('CreateBrandGroupPublicationModal', setState)}
          showModal={showCreateBrandGroupPublicationModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
